/**
 * AI ACTIVITY LOG
 * 
 * Timeline showing AI agent steps, models used, and reasoning.
 * Linear.app inspired design with refined timeline and typography.
 */

import { useState } from "react";
import { type AIActivity, type AIModel, type AIStep } from "@/stores/reviewStore";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Bot,
  Clock,
  Code2,
  Eye,
  FileSearch,
  Lightbulb,
  RefreshCw,
  TestTube,
  Bug,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ModelStyle = { label: string; color: string; badgeClass: string; dotColor: string };

const defaultModelStyle: ModelStyle = {
  label: "AI",
  color: "text-muted-foreground",
  badgeClass: "bg-muted/40 text-muted-foreground border-border/50",
  dotColor: "border-muted-foreground",
};

const modelConfig: Record<string, ModelStyle> = {
  "claude-opus-4.6": { label: "Opus 4.6", color: "text-orange-400", badgeClass: "bg-orange-500/12 text-orange-400 border-orange-500/20", dotColor: "border-orange-400" },
  "claude-sonnet-4.5": { label: "Sonnet 4.5", color: "text-orange-400", badgeClass: "bg-orange-500/12 text-orange-400 border-orange-500/20", dotColor: "border-orange-400" },
  "claude-haiku-4.5": { label: "Haiku 4.5", color: "text-orange-300", badgeClass: "bg-orange-500/12 text-orange-300 border-orange-500/20", dotColor: "border-orange-300" },
  "claude-code": { label: "Claude Code", color: "text-orange-400", badgeClass: "bg-orange-500/12 text-orange-400 border-orange-500/20", dotColor: "border-orange-400" },
  "codex-5.3": { label: "Codex 5.3", color: "text-violet-400", badgeClass: "bg-violet-500/12 text-violet-400 border-violet-500/20", dotColor: "border-violet-400" },
  o3: { label: "o3", color: "text-emerald-400", badgeClass: "bg-emerald-500/12 text-emerald-400 border-emerald-500/20", dotColor: "border-emerald-400" },
  "gpt-4o": { label: "GPT-4o", color: "text-emerald-400", badgeClass: "bg-emerald-500/12 text-emerald-400 border-emerald-500/20", dotColor: "border-emerald-400" },
  "gemini-2.5-pro": { label: "Gemini 2.5 Pro", color: "text-blue-400", badgeClass: "bg-blue-500/12 text-blue-400 border-blue-500/20", dotColor: "border-blue-400" },
  "gemini-2.5-flash": { label: "Gemini 2.5 Flash", color: "text-blue-300", badgeClass: "bg-blue-500/12 text-blue-300 border-blue-500/20", dotColor: "border-blue-300" },
  local: { label: "Local", color: "text-muted-foreground", badgeClass: "bg-muted/40 text-muted-foreground border-border/50", dotColor: "border-muted-foreground" },
  // Legacy
  claude: { label: "Claude", color: "text-orange-400", badgeClass: "bg-orange-500/12 text-orange-400 border-orange-500/20", dotColor: "border-orange-400" },
  codex: { label: "Codex", color: "text-violet-400", badgeClass: "bg-violet-500/12 text-violet-400 border-violet-500/20", dotColor: "border-violet-400" },
  "gpt-4": { label: "GPT-4", color: "text-emerald-400", badgeClass: "bg-emerald-500/12 text-emerald-400 border-emerald-500/20", dotColor: "border-emerald-400" },
  "gpt-3.5": { label: "GPT-3.5", color: "text-teal-400", badgeClass: "bg-teal-500/12 text-teal-400 border-teal-500/20", dotColor: "border-teal-400" },
  gemini: { label: "Gemini", color: "text-blue-400", badgeClass: "bg-blue-500/12 text-blue-400 border-blue-500/20", dotColor: "border-blue-400" },
};

const actionConfig: Record<AIStep["action"], { label: string; icon: typeof Bot }> = {
  plan: { label: "Plan", icon: Lightbulb },
  code: { label: "Code", icon: Code2 },
  review: { label: "Review", icon: Eye },
  revise: { label: "Revise", icon: RefreshCw },
  test: { label: "Test", icon: TestTube },
  debug: { label: "Debug", icon: Bug },
};

interface AIActivityLogProps {
  activity: AIActivity;
}

export function AIActivityLog({ activity }: AIActivityLogProps) {
  const [expandedReasoning, setExpandedReasoning] = useState<Set<string>>(new Set());

  const toggleReasoning = (stepId: string) => {
    setExpandedReasoning((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  const formatTokens = (tokens?: number) => {
    if (!tokens) return null;
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  return (
    <div className="space-y-4 overflow-hidden">
      <div className="flex items-center gap-3 p-3 rounded-lg linear-section overflow-hidden">
        <Bot className="w-4 h-4 opacity-60 flex-shrink-0" />
        <span className="text-sm text-muted-foreground/80 break-words min-w-0">
          <span className="tabular-nums font-medium text-foreground/90">{activity.totalSteps}</span> steps
          {activity.totalRevisions > 0 && (
            <>, <span className="tabular-nums font-medium text-foreground/90">{activity.totalRevisions}</span> revision{activity.totalRevisions > 1 ? "s" : ""}</>
          )}
          , <span className="tabular-nums font-medium text-foreground/90">{activity.modelsUsed.length}</span> AI model{activity.modelsUsed.length > 1 ? "s" : ""}
          , <span className="tabular-nums font-medium text-foreground/90">{activity.totalDuration}</span> total
        </span>
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        <AccordionItem value="timeline" className="linear-accordion px-4 overflow-hidden">
          <AccordionTrigger className="hover:no-underline py-3.5">
            <div className="flex items-center gap-3 min-w-0 flex-wrap">
              <Zap className="w-4 h-4 opacity-60 flex-shrink-0" />
              <span className="font-medium text-sm">AI Activity Timeline</span>
              <div className="flex gap-1.5 flex-wrap">
                {activity.modelsUsed.map((model) => (
                  <span
                    key={model}
                    className={cn(
                      "linear-badge text-xs",
                      (modelConfig[model] || defaultModelStyle).badgeClass
                    )}
                  >
                    {(modelConfig[model] || defaultModelStyle).label}
                  </span>
                ))}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="relative">
              <div className="absolute left-[11px] top-3 bottom-3 w-px linear-timeline-line" />
              <div className="space-y-4">
                {activity.steps.map((step, index) => {
                  const ActionIcon = actionConfig[step.action].icon;
                  const model = modelConfig[step.model] || defaultModelStyle;
                  const hasReasoning = !!step.reasoning;
                  const isReasoningExpanded = expandedReasoning.has(step.id);

                  return (
                    <div key={step.id} className="relative pl-9">
                      <div
                        className={cn(
                          "absolute left-0 w-[22px] h-[22px] rounded-full border-2 bg-card flex items-center justify-center linear-timeline-dot",
                          model.dotColor
                        )}
                      >
                        <div className={cn("w-2 h-2 rounded-full", model.color.replace("text-", "bg-"))} />
                      </div>
                      <div className="space-y-2 pt-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn("linear-badge text-xs", model.badgeClass)}>
                            {model.label}
                          </span>
                          <span className="linear-badge-muted text-xs gap-1">
                            <ActionIcon className="w-3 h-3" />
                            {actionConfig[step.action].label}
                          </span>
                          {step.revision && (
                            <span className="linear-badge-warning text-xs">
                              Rev {step.revision}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground/60 flex items-center gap-1.5 ml-auto">
                            <Clock className="w-3 h-3" />
                            <span className="tabular-nums">{step.duration}</span>
                          </span>
                        </div>
                        <p className="text-sm text-foreground/90 leading-relaxed">{step.summary}</p>
                        {(step.inputTokens || step.outputTokens) && (
                          <div className="text-xs text-muted-foreground/50 tabular-nums">
                            {step.inputTokens && <span>↓{formatTokens(step.inputTokens)}</span>}
                            {step.inputTokens && step.outputTokens && <span className="mx-1 opacity-50">/</span>}
                            {step.outputTokens && <span>↑{formatTokens(step.outputTokens)}</span>}
                            <span className="ml-1 opacity-70">tokens</span>
                          </div>
                        )}
                        {hasReasoning && (
                          <div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2.5 text-xs text-muted-foreground/60 hover:text-foreground hover:bg-muted/30 transition-all duration-150"
                              onClick={() => toggleReasoning(step.id)}
                            >
                              <FileSearch className="w-3 h-3 mr-1.5" />
                              {isReasoningExpanded ? "Hide" : "Show"} reasoning
                              {isReasoningExpanded ? (
                                <ChevronUp className="w-3 h-3 ml-1.5" />
                              ) : (
                                <ChevronDown className="w-3 h-3 ml-1.5" />
                              )}
                            </Button>
                            {isReasoningExpanded && (
                              <div className="mt-2.5 p-3 rounded-lg bg-muted/30 border border-border/40 text-sm text-muted-foreground/80 leading-relaxed">
                                {step.reasoning}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
